import { Component, ContentChild, HostBinding, Input, OnInit } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { untilDestroy } from '@tokeet-frontend/tv3-platform'
import { map } from 'rxjs/operators'
import {
  AutomationEventTypes,
  AutomationHandler,
  AutomationHelpers,
  AutomationServiceTypes,
  AutomationTypes,
  AutomationViewGroup,
  selectAutomationGroupsByType,
} from '@tokeet-frontend/automations'
import { Observable } from 'rxjs'
import * as lodash from 'lodash'
import { AutopilotAutomationGroupItemDirective } from '@tv3/containers/automations/automations-wizard/short-automations/autopilot-automations-group/autopilot-automation-group-item.directive'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { AutomationDestroyableSaver } from '@tv3/containers/automations/automation-destroyable-saver'
import { AutopilotAutomationConfigurableData } from '@tv3/containers/automations/automations-wizard/short-automations/autopilot-automation.component'

@Component({
  selector: 'app-autopilot-automations-group',
  templateUrl: './autopilot-automations-group.component.html',
  styleUrls: ['./autopilot-automations-group.component.scss'],
})
export class AutopilotAutomationsGroupComponent extends AutomationDestroyableSaver implements OnInit {
  @Input() label: string
  @Input() name: string
  @Input() addLabel = $localize`Add Automation`
  //
  @Input() type: AutomationTypes = AutomationTypes.Message
  @Input() service = AutomationServiceTypes.Advance
  @Input() event: AutomationEventTypes
  @Input() handler: AutomationHandler = AutomationHandler.SendEmailMessage

  @Input() initData: Partial<AutopilotAutomationConfigurableData>
  @Input() days = 0
  @Input() hours = 0
  @Input() rentals: string[] = []
  @Input() channels: string[] = []

  @HostBinding('class') get cssClass() {
    return this.viewType === 'table' ? 'table-view' : 'list-view'
  }

  @Input() viewType: 'table' | 'list' = 'list'

  @Input() idPrefix: string
  @Input() idSuffix: string

  automations$: Observable<AutomationViewGroup[]>
  automationGroups: AutomationViewGroup[] = []

  trackByGroupId = (t: AutomationViewGroup) => t.group_id

  @ContentChild(AutopilotAutomationGroupItemDirective) automationItemDirective: AutopilotAutomationGroupItemDirective

  constructor(protected store: Store<any>, protected amplitudeService: AmplitudeService) {
    super(store, amplitudeService)
  }

  ngOnInit(): void {
    this.automations$ = this.store.pipe(
      select(selectAutomationGroupsByType(this.type, false)),
      map((items) =>
        items.filter((item) => {
          return item.service === this.service && item.event === this.event && item.handler === this.handler
        })
      ),
      map((items) => lodash.filter(items, (item) => item.attributes?.autopilot === this.getAutopilotId())),
      map((items) => lodash.sortBy(items, (t) => t.created))
    )
    this.automations$.pipe(untilDestroy(this)).subscribe((items) => {
      this.automationGroups = items
    })
  }

  saveAutomation(formData: Partial<AutopilotAutomationConfigurableData>) {
    const isTimeEvent = AutomationHelpers.isTimeEvent(this.event)
    this.save({
      ...formData,
      status: true,
      service: this.service,
      rentals: this.rentals || [],
      channels: this.channels || [],
      name: this.name || this.label,
      handler: this.handler,
      event: isTimeEvent ? AutomationEventTypes.TimeBasedEvent : this.event,
      timeevent: isTimeEvent ? { days: this.days || 0, hours: this.hours || 0, event: this.event } : undefined,
      type: this.type,
      attributes: {
        autopilot: this.getAutopilotId(),
      },
    })
  }

  onAdd() {
    this.saveAutomation(this.initData)
  }

  getAutopilotId() {
    return `${this.idPrefix || 'autopilot'}|${this.service}|${this.type}|${this.event}|${this.handler}|${
      this.idSuffix || ''
    }`
  }
}
